import {
  useTimeSlotChoices,
  useUtilityRequestConfiguration,
  useConsumerMetaData,
} from "@features/cx/reconnect/hooks";
import { CreateReconnectionRequestPayload } from "@features/cx/reconnect/types";
import { reconnectionApi } from "@features/cx/reconnect/api";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  Search,
  User,
  MapPin,
  CheckCircle2,
  Mail,
  Users,
  Phone,
  LucideUserSquare,
  LetterText,
  Key,
} from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchInput } from "@shared/components/SearchInput";
import { Badge } from "@shared/ui/badge";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { formatTerritoryAddress } from "@shared/utils/address";

interface RequestDetailsStepProps {
  remoteUtilityId: number;
  storageKey: string;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export function RequestDetailsStep({
  remoteUtilityId,
  storageKey,
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}: RequestDetailsStepProps) {
  const [isStepDataLoaded, setIsStepDataLoaded] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [selectedMasterId, setSelectedMasterId] = useState<number | null>(null);
  const [isMasterMetadataProcessed, setIsMasterMetadataProcessed] =
    useState(false);

  // ✅ FIX: masterData is now local state, same pattern as customerData in CustomerInformationStep
  const [masterData, setMasterData] = useState<{
    accountNumber: string;
    customerName: string;
    serviceAddress: string;
    contactPhone: string;
    contactEmail: string;
    status: string;
    ownershipType: string;
  } | null>(null);

  const ownershipDisplay = useMemo(() => {
    if (!customerData) return "Individual";

    // Check metadata flags first as they are more reliable
    const metadata = customerData.consumerMetadata;
    if (metadata?.isTenant) return "Tenant";
    if (metadata?.isSubAccount) return "Sub-Account";

    // Fallback to string display or default to Individual
    return customerData?.ownershipDisplay || "Individual";
  }, [customerData]);

  const consumerAddressString = useMemo(() => {
    if (!customerData) return "";
    const territoryData =
      customerData.territoryData ||
      customerData.consumerAddress ||
      customerData.consumerMetadata?.territoryData ||
      customerData.consumerMetadata?.consumerAddress;

    return (
      formatTerritoryAddress(territoryData, "service") ||
      (typeof customerData.serviceAddress === "string"
        ? customerData.serviceAddress
        : "")
    );
  }, [customerData]);

  const [selectedOwner, setSelectedOwner] = useState<any>(null);
  const [isOwnerSelected, setIsOwnerSelected] = useState(false);
  const [showOwnerResults, setShowOwnerResults] = useState(false);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState("");
  const dataLoadedRef = useRef(false);
  const savedStepDataRef = useRef<any>(null);
  const isProcessingChangeRef = useRef(false);

  // Get master consumer meta data
  const { data: masterMetadata } = useConsumerMetaData({
    consumerId: selectedMasterId || 0,
    remoteUtilityId,
  });

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: Partial<CreateReconnectionRequestPayload>) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("Request details step data saved:", stepData);
      } catch (error) {
        console.error("Failed to save request details step data:", error);
      }
    },
    [stepHelpers, currentStepIndex],
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get request details step data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Helper function to get customer data from previous step
  const getCustomerData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      const customerStepData = stepHelpers.getStepData(0); // Customer info is step 0
      return customerStepData;
    } catch (error) {
      console.error("Failed to get customer data from previous step:", error);
      return null;
    }
  }, [stepHelpers]);

  // Handle master consumer search logic (direct API call for reliable dropdown)
  const handleMasterSearch = useCallback(
    async (term: string) => {
      if (term.length < 3) return [];

      try {
        const response = await reconnectionApi.searchMasterConsumers({
          remoteUtilityId,
          searchData: term,
        });

        const results = response?.result || [];

        return results.map((acc: any) => ({
          id: acc.id.toString(),
          label:
            `${acc.firstName || ""} ${acc.lastName || ""}`.trim() ||
            "Unknown Consumer",
          description: `Acc: ${acc.consumerNo || "N/A"}`,
          accountNo: acc.consumerNo,
          name: `${acc.firstName || ""} ${acc.lastName || ""}`.trim(),
          status: acc.statusDisplay || "Active",
          address: acc.serviceAddress?.address || "",
        }));
      } catch (error) {
        console.error("Master search failed:", error);
        return [];
      }
    },
    [remoteUtilityId],
  );

  // Handle master account selection
  const handleSelectMaster = useCallback(
    (acc: any, form: any) => {
      const masterId = parseInt(acc.id);
      setSelectedMasterId(masterId);
      setMasterData(null); // ✅ Clear stale data while new metadata loads

      const masterObj = {
        id: masterId,
        name: acc.name || acc.label || "",
        accountNo: acc.accountNo || acc.accountNumber || acc.consumerNo || "",
        address: acc.address || acc.description || "",
        status: acc.status || "Active",
      };

      form.setValue(
        "newMasterAccountNumber",
        acc.accountNo || acc.accountNumber || acc.consumerNo || "",
      );
      form.setValue("selectedMaster", masterObj);

      // Save to step data immediately for persistence
      saveStepData({
        ...form.getValues(),
        newMasterAccountNumber:
          acc.accountNo || acc.accountNumber || acc.consumerNo || "",
        selectedMaster: masterObj,
      });
    },
    [saveStepData],
  );

  // Render master search result
  const renderMasterResult = useCallback(
    (result: any) => (
      <div className="p-3 hover:bg-blue-50 cursor-pointer rounded border-l-4 border-transparent hover:border-blue-400 transition-all">
        <div className="font-medium text-gray-900">{result.name}</div>
        <div className="text-sm text-gray-600">Account {result.accountNo}</div>
        {result.status && (
          <div className="text-xs text-gray-500">{result.status}</div>
        )}
      </div>
    ),
    [],
  );

  // ✅ FIX: Reset masterData state AND processed flag when selectedMasterId changes
  useEffect(() => {
    if (selectedMasterId) {
      setIsMasterMetadataProcessed(false);
      setMasterData(null); // Clear stale data while new metadata loads
    }
  }, [selectedMasterId]);

  // Fetch utility request configuration for reasons
  const { data: utilityConfigData, isLoading: isConfigLoading } =
    useUtilityRequestConfiguration({
      remoteUtilityId,
      requestType: "Reconnection",
      disablePagination: true,
    });

  // Fetch time slot choices
  const { data: timeSlotData, isLoading: isTimeSlotLoading } =
    useTimeSlotChoices();

  const isTenantAccount = useCallback(
    () => ownershipDisplay === "Tenant",
    [ownershipDisplay],
  );

  const showMasterAccountField = useCallback(
    (values: any) => {
      return (
        (ownershipDisplay === "Sub-Account" &&
          values.accountStructurePreference === "link_different") ||
        ((ownershipDisplay === "Individual" || ownershipDisplay === "Master") &&
          values.accountStructurePreference === "link_master")
      );
    },
    [ownershipDisplay],
  );

  const showOwnerSearchField = useCallback(
    (values: any) => {
      return (
        ownershipDisplay === "Tenant" &&
        values.accountStructurePreference === "link_different_owner"
      );
    },
    [ownershipDisplay],
  );

  const showNewServiceAddressFields = useCallback((values: any) => {
    return values.serviceAreaPreference === "new";
  }, []);

  const showDifferentMailingAddressFields = useCallback((values: any) => {
    return values.mailingAddressPreference === "different";
  }, []);

  // Get dynamic options for account structure based on account type
  const getAccountStructureOptions = useCallback(() => {
    if (ownershipDisplay === "Sub-Account") {
      return [
        {
          label: "Keep under the same master account",
          value: "keep_same_master",
          description:
            "The reconnected service will remain linked to the current master account with no changes to billing hierarchy.",
        },
        {
          label: "Link to a different master account",
          value: "link_different",
          description:
            "Transfer this sub-account to a different master account upon reconnection.",
        },
        {
          label: "Reconnect as an Individual account",
          value: "individual",
          description:
            "Detach from the master account and reconnect as a standalone individual account.",
        },
      ];
    }
    if (ownershipDisplay === "Individual" || ownershipDisplay === "Master") {
      return [
        {
          label: "Yes, link to a master account",
          value: "link_master",
          description:
            "Link this account under an existing master account for consolidated billing and shared resources.",
        },
        {
          label: "No, continue as individual account",
          value: "no_change",
          description:
            "Reconnect as a standalone account with independent billing and service agreements.",
        },
      ];
    }
    if (ownershipDisplay === "Tenant") {
      return [
        {
          label: "Stay under the same owner",
          value: "keep_same_owner",
          description:
            "The reconnected service will remain linked to the current owner account.",
        },
        {
          label: "Link to a different owner",
          value: "link_different_owner",
          description:
            "Transfer this tenant account to a different property owner upon reconnection.",
        },
        {
          label: "Reconnect as an Individual account",
          value: "individual",
          description:
            "Detach from the owner account and reconnect as a standalone individual account.",
        },
      ];
    }
    return [];
  }, [ownershipDisplay]);

  // Define form fields using FormService pattern
  const requestFields: FormField[] = useMemo(
    () => [
      {
        name: "reason",
        label: "Reason",
        type: "select",
        required: true,
        placeholder: "Select a reason",
        options:
          utilityConfigData?.result?.map((config) => ({
            label: config.name,
            value: config.id.toString(),
          })) || [],
        className: "col-span-12",
      },
      {
        name: "scheduledDate",
        label: "Preferred Date",
        type: "datepicker",
        placeholder: "Select date",
        allowPast: false,
        helperText: "Choose your preferred date for the service visit",
        showHelperTooltip: true,
        tooltipText: "Field staff will attempt to accommodate your preference.",
        className: "col-span-6",
      },
      {
        name: "timeSlot",
        label: "Preferred Time Slot",
        type: "select",
        placeholder: "Select time slot",
        className: "col-span-6",
        options:
          timeSlotData?.result?.map((slot) => ({
            label: slot.value,
            value: slot.key.toString(),
          })) || [],
        helperText: "Select your preferred time window",
      },
      {
        name: "consumerRemark",
        label: "Additional Notes",
        type: "textarea",
        placeholder: "Add special instructions or important information...",
        rows: 4,
        helperText: "Provide any additional information for field staff",
        showHelperTooltip: true,
        tooltipText: "Include access instructions or special considerations.",
        className: "col-span-12",
      },
      {
        name: "accountStructurePreferenceSection",
        type: "custom",
        className: "col-span-12",
        showWhen: () =>
          ["Sub-Account", "Individual", "Tenant"].includes(ownershipDisplay),
        customRender: () => (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3 border-b border-gray-100 pb-2">
              <CheckCircle2 className="h-4 w-4 text-blue-700" />
              <h4 className="text-xs font-black text-blue-900 uppercase tracking-[0.1em]">
                Account Structure Preference
              </h4>
            </div>
            <p className="text-sm font-bold text-gray-800 mb-4">
              {ownershipDisplay === "Sub-Account" &&
                "This account is currently linked under a master account. How would you like to proceed with this reconnection?"}
              {ownershipDisplay === "Individual" &&
                "Would you like to link this reconnection under a master account?"}
              {ownershipDisplay === "Tenant" &&
                "This account is currently linked under an owner account. How would you like to proceed with this reconnection?"}
            </p>
          </div>
        ),
      },
      {
        name: "currentOwnerInfo",
        type: "custom",
        className: "col-span-12",
        showWhen: isTenantAccount,
        customRender: () => {
          const metadata = customerData?.consumerMetadata;
          return (
            <div className="mb-4 p-4 bg-[#F0F7FF] border border-blue-100 rounded-xl">
              <h5 className="text-xs font-black text-blue-700 uppercase mb-3 flex items-center gap-2 tracking-widest">
                <User className="h-3 w-3" />
                Current Owner Information
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <ValueLabel
                  label="Owner Name"
                  value={metadata?.ownerName ?? "N/A"}
                />
                <ValueLabel
                  label="Owner Account Number"
                  value={metadata?.ownerAccountNo ?? "N/A"}
                />
                <div className="md:col-span-2">
                  <ValueLabel
                    label="Owner Address"
                    value={metadata?.ownerAddress ?? "N/A"}
                  />
                </div>
              </div>
            </div>
          );
        },
      },
      {
        name: "accountStructurePreference",
        type: "radio-card",
        className: "col-span-12",
        showWhen: () =>
          ["Sub-Account", "Individual", "Tenant", "Master"].includes(
            ownershipDisplay,
          ),
        options: getAccountStructureOptions(),
        classes: {
          container: "mb-2",
          radioGroup: `grid grid-cols-1 ${
            ownershipDisplay === "Individual" || ownershipDisplay === "Master"
              ? "md:grid-cols-2"
              : "md:grid-cols-3"
          } gap-4 w-full`,
          radioCard:
            "flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-blue-300 bg-white w-full",
          radioCardChecked:
            "border-blue-500 bg-blue-50/70 shadow-sm ring-1 ring-blue-500",
          label: "font-bold text-gray-900 text-[14px] leading-tight",
          description:
            "text-[12px] text-gray-500 mt-1 font-medium leading-normal",
        },
      },
      {
        name: "newMasterAccountNumber",
        type: "custom",
        className: "col-span-12 -mt-2",
        showWhen: showMasterAccountField,
        customRender: (field: FormField, form: any) => {
          const isMasterSelected = !!form.watch("newMasterAccountNumber");

          return (
            <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
              {!isMasterSelected ? (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      Master Account Search
                    </h3>
                  </div>

                  <div className="relative">
                    <SearchInput
                      placeholder="Search by master account number, name or address..."
                      onSearch={handleMasterSearch}
                      onSelect={(acc) => handleSelectMaster(acc, form)}
                      renderResult={renderMasterResult}
                      minSearchLength={3}
                      className="w-full"
                      debounceMs={200}
                    />
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Type at least 3 characters to search master accounts
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 shadow-sm animate-in fade-in zoom-in duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="h-4 w-4 text-green-600" />
                    <h4 className="font-medium text-green-800">
                      Selected Master Account
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        form.setValue("newMasterAccountNumber", "");
                        form.setValue("selectedMaster", null);
                        setSelectedMasterId(null);
                        setMasterData(null); // ✅ Clear displayed data on change
                      }}
                      className="ml-auto text-[10px] font-black text-blue-600 hover:text-blue-800 transition-colors uppercase tracking-widest bg-white px-3 py-1 rounded-lg border border-blue-200 shadow-sm hover:shadow-md active:scale-95"
                    >
                      Change
                    </button>
                  </div>

                  {/* ✅ FIX: Reads from masterData local state — always up to date from API */}
                  {masterData ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <LucideUserSquare className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          Account:
                        </span>
                        <span className="text-gray-900">
                          {masterData.accountNumber || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <LetterText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-700">Name:</span>
                        <span className="text-gray-900">
                          {masterData.customerName || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          Phone:
                        </span>
                        <span className="text-gray-900">
                          {masterData.contactPhone || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          Email:
                        </span>
                        <span className="text-gray-900">
                          {masterData.contactEmail || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          Status:
                        </span>
                        <span className="text-gray-900">
                          {masterData.status || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          Owner Type:
                        </span>
                        <span className="text-gray-900">
                          {masterData.ownershipType || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 md:col-span-2">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          Address:
                        </span>
                        <span className="text-gray-900">
                          {masterData.serviceAddress || "N/A"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    // ✅ Loading state while metadata is being fetched
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      <span>Loading master account details...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        name: "newOwnerName",
        type: "text",
        label: "New Owner Name",
        placeholder: "Enter full name",
        showWhen: (values: any) =>
          ownershipDisplay === "Tenant" &&
          values.accountStructurePreference === "link_different_owner",
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "newOwnerEmail",
        type: "text",
        label: "New Owner Email",
        placeholder: "owner@example.com",
        showWhen: (values: any) =>
          ownershipDisplay === "Tenant" &&
          values.accountStructurePreference === "link_different_owner",
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "newOwnerPhone",
        type: "text",
        label: "New Owner Phone",
        placeholder: "+685 1234567",
        showWhen: (values: any) =>
          ownershipDisplay === "Tenant" &&
          values.accountStructurePreference === "link_different_owner",
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "newOwnerAddress",
        type: "text",
        label: "New Owner Address",
        placeholder: "Enter full address",
        showWhen: (values: any) =>
          ownershipDisplay === "Tenant" &&
          values.accountStructurePreference === "link_different_owner",
        className: "col-span-12 md:col-span-6",
      },
      // SERVICE AREA SECTION
      {
        name: "serviceAreaHeader",
        type: "custom",
        className: "col-span-12",
        customRender: () => (
          <div className="mb-2 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold  tracking-[0.1em]">
                Service Area
              </h4>
            </div>
            <p className="text-sm text-gray-800 font-semibold mb-2">
              Is this reconnection for the same service area linked to the
              account?
            </p>
          </div>
        ),
      },
      {
        name: "serviceAreaPreference",
        type: "radio-card",
        className: "col-span-12",
        options: [
          {
            label: "Same Service Area",
            value: "same",
            description: consumerAddressString
              ? `Reconnect at the existing address: ${consumerAddressString}`
              : "Reconnect at the existing address linked to this account",
          },
          {
            label: "New Service Area",
            value: "new",
            description:
              "Reconnect at a different address not linked to this account",
          },
        ],
        classes: {
          container: "mb-2",
          radioGroup: "grid grid-cols-1 md:grid-cols-2 gap-4 w-full",
          radioCard:
            "flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-blue-300 bg-white w-full",
          radioCardChecked:
            "border-blue-500 bg-blue-50/70 shadow-sm ring-1 ring-blue-500",
          label: "font-bold text-gray-900 text-[14px] leading-tight",
          description:
            "text-[12px] text-gray-500 mt-1 font-medium leading-normal",
        },
      },
      {
        name: "newHouseNumber",
        type: "text",
        label: "House/Unit Number",
        placeholder: "e.g. 742",
        showWhen: showNewServiceAddressFields,
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "newZipCode",
        type: "text",
        label: "Zip/Postal Code",
        placeholder: "e.g. 62704",
        showWhen: showNewServiceAddressFields,
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "newCity",
        type: "text",
        label: "City",
        placeholder: "Springfield",
        showWhen: showNewServiceAddressFields,
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "newStreetAddress",
        type: "text",
        label: "Street Address",
        placeholder: "Evergreen Terrace",
        showWhen: showNewServiceAddressFields,
        className: "col-span-12 md:col-span-6",
      },
      // MAILING ADDRESS SECTION
      {
        name: "mailingAddressHeader",
        type: "custom",
        className: "col-span-12",
        customRender: () => (
          <div className="mb-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold  tracking-[0.1em]">
                Mailing Address
              </h4>
            </div>
            <p className="text-sm text-gray-800 font-semibold mb-2">
              Where should correspondence and bills be sent?
            </p>
          </div>
        ),
      },
      {
        name: "mailingAddressPreference",
        type: "radio-card",
        className: "col-span-12",
        options: [
          {
            label: "Same as Service Address",
            value: "same_as_service",
            description:
              "Use the service area address for all mailing and billing correspondence",
          },
          {
            label: "Different Mailing Address",
            value: "different",
            description:
              "Send correspondence to a different address than the service location",
          },
        ],
        classes: {
          container: "mb-2",
          radioGroup: "grid grid-cols-1 md:grid-cols-2 gap-4 w-full",
          radioCard:
            "flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-blue-300 bg-white w-full",
          radioCardChecked:
            "border-blue-500 bg-blue-50/70 shadow-sm ring-1 ring-blue-500",
          label: "font-bold text-gray-900 text-[14px] leading-tight",
          description:
            "text-[12px] text-gray-500 mt-1 font-medium leading-normal",
        },
      },
      {
        name: "mailingHouseNumber",
        type: "text",
        label: "House/Unit Number",
        placeholder: "e.g. 742",
        showWhen: showDifferentMailingAddressFields,
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "mailingZipCode",
        type: "text",
        label: "Zip/Postal Code",
        placeholder: "62704",
        showWhen: showDifferentMailingAddressFields,
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "mailingCity",
        type: "text",
        label: "City",
        placeholder: "Springfield",
        showWhen: showDifferentMailingAddressFields,
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "mailingStreetAddress",
        type: "text",
        label: "Street Address",
        placeholder: "PO Box 123",
        showWhen: showDifferentMailingAddressFields,
        className: "col-span-12 md:col-span-6",
      },
    ],
    [
      utilityConfigData,
      timeSlotData,
      ownershipDisplay,
      customerData,
      consumerAddressString,
      masterData, // ✅ masterData now in deps so card re-renders when API data arrives
      showMasterAccountField,
      showOwnerSearchField,
      isTenantAccount,
      showNewServiceAddressFields,
      showDifferentMailingAddressFields,
      isOwnerSelected,
      selectedOwner,
      handleMasterSearch,
      handleSelectMaster,
      renderMasterResult,
    ],
  );

  // Create form instance using FormService
  const requestForm = FormService.createForm<CreateReconnectionRequestPayload>({
    fields: requestFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      requestType: "disconnection",
      utilitySupportRequest: 0,
      reason: "",
      priority: "medium",
      scheduledDate: null,
      timeSlot: "",
      consumerRemark: "",
      serviceAddress: "",
      consumer: null,
      contactPhone: "",
      contactEmail: "",
      contactName: "",
      contactId: null,
      contactType: "",
      accountStructurePreference:
        ownershipDisplay === "Tenant"
          ? "same_owner"
          : ownershipDisplay === "Sub-Account"
            ? "same_master"
            : "individual_no_link",
      serviceAreaPreference: "same_area",
      mailingAddressPreference: "same_as_service",
      newMasterAccountNumber: "",
      selectedOwnerId: "",
      selectedMaster: null,
      selectedOwner: null,
      selectedConsumerId: null,
    },
  });

  // ✅ FIX: Process master metadata into local state — mirrors CustomerInformationStep exactly
  useEffect(() => {
    if (
      masterMetadata?.result &&
      selectedMasterId &&
      !isMasterMetadataProcessed
    ) {
      const metadata = masterMetadata.result;

      const firstName = metadata.firstName || "";
      const lastName = metadata.lastName || "";
      const consumerNo = metadata.consumerNo || "";
      const contactNumber = metadata.contactNumber || "";
      const email = metadata.email || "";
      const statusDisplay = metadata.statusDisplay || "N/A";
      const ownershipType = metadata.ownershipDisplay || "N/A";
      const serviceAddress = metadata.serviceAddress;

      const addressParts = [
        serviceAddress?.premise,
        serviceAddress?.unit,
        serviceAddress?.area,
        serviceAddress?.subArea,
        serviceAddress?.division,
        serviceAddress?.zone,
        serviceAddress?.county,
        serviceAddress?.state,
        serviceAddress?.zipcode,
      ].filter(Boolean);

      const fullAddress =
        serviceAddress?.address ||
        (addressParts.length > 0 ? addressParts.join(", ") : "N/A");

      // ✅ Set local state directly — same as CustomerInformationStep's setCustomerData(formData)
      const resolvedMasterData = {
        accountNumber: consumerNo,
        customerName: `${firstName} ${lastName}`.trim() || "N/A",
        serviceAddress: fullAddress,
        contactPhone: contactNumber,
        contactEmail: email,
        status: statusDisplay,
        ownershipType,
      };

      setMasterData(resolvedMasterData);

      // Keep form value in sync for submission payload
      requestForm.setValue("selectedMaster", {
        id: metadata.id,
        name: resolvedMasterData.customerName,
        accountNo: consumerNo,
        address: fullAddress,
        status: statusDisplay,
        email,
        phone: contactNumber,
        ownershipDisplay: ownershipType,
      });

      setIsMasterMetadataProcessed(true);

      saveStepData({
        ...requestForm.getValues(),
        selectedMaster: requestForm.getValues("selectedMaster"),
      });
    }
  }, [
    masterMetadata,
    selectedMasterId,
    isMasterMetadataProcessed,
    requestForm,
    saveStepData,
  ]);

  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        const customerStepData = getCustomerData();

        // Store saved data in ref for use in other effects
        savedStepDataRef.current = savedStepData;

        if (savedStepData) {
          console.log("Loaded request details step data:", savedStepData);

          // Populate form with saved data including labels
          const formDataToSet = {
            ...savedStepData,
            requestType: savedStepData.requestType || "reconnect",
            utilitySupportRequest: savedStepData.utilitySupportRequest || 0,
            reason: savedStepData.reason || "",
            priority: savedStepData.priority || "medium",
            scheduledDate: savedStepData.scheduledDate || null,
            timeSlot: savedStepData.timeSlot || "",
            consumerRemark: savedStepData.consumerRemark || "",
            serviceAddress: savedStepData.serviceAddress || "",
            consumer: savedStepData.consumer || null,
            contactPhone: savedStepData.contactPhone || "",
            contactEmail: savedStepData.contactEmail || "",
            contactName: savedStepData.contactName || "",
            contactId: savedStepData.contactId || null,
            contactType: savedStepData.contactType || "",
            reasonLabel: savedStepData.reasonLabel || "",
            timeSlotLabel: savedStepData.timeSlotLabel || "",
          };

          FormService.populateForm(requestForm, formDataToSet, requestFields);

          // Update the field options with labels if they exist in saved data
          if (savedStepData.reasonLabel && savedStepData.reason) {
            const reasonField = requestFields.find(
              (field) => field.name === "reason",
            );
            if (reasonField) {
              const optionExists = reasonField.options?.some(
                (option) => option.value === savedStepData.reason,
              );
              if (!optionExists) {
                reasonField.options = [
                  ...(reasonField.options || []),
                  {
                    label: savedStepData.reasonLabel,
                    value: savedStepData.reason,
                  },
                ];
              }
            }
          }

          if (savedStepData.timeSlotLabel && savedStepData.timeSlot) {
            const timeSlotField = requestFields.find(
              (field) => field.name === "timeSlot",
            );
            if (timeSlotField) {
              const optionExists = timeSlotField.options?.some(
                (option) => option.value === savedStepData.timeSlot,
              );
              if (!optionExists) {
                timeSlotField.options = [
                  ...(timeSlotField.options || []),
                  {
                    label: savedStepData.timeSlotLabel,
                    value: savedStepData.timeSlot,
                  },
                ];
              }
            }
          }

          // Restore custom selection states
          if (savedStepData.selectedOwner) {
            setSelectedOwner(savedStepData.selectedOwner);
            setIsOwnerSelected(true);
          }
          if (savedStepData.selectedMaster) {
            setSelectedMasterId(Number(savedStepData.selectedMaster.id));
          }
        }

        // Load customer data and service address from previous step
        if (customerStepData) {
          console.log(
            "Loaded customer data for request details:",
            customerStepData,
          );
          setCustomerData(customerStepData);

          // Update service address from customer data if not already set
          if (!savedStepData?.serviceAddress) {
            requestForm.setValue(
              "serviceAddress",
              customerStepData.serviceAddress?.address || "",
            );
          }

          // If we have consumer metadata, use it
          if (customerStepData.consumerMetadata && !savedStepData?.consumer) {
            const metadata = customerStepData.consumerMetadata;
            requestForm.setValue("consumer", {
              id: customerStepData.selectedConsumerId,
              consumerNo: customerStepData.accountNumber,
              email: customerStepData.contactEmail,
              firstName: metadata.firstName,
              lastName: metadata.lastName,
              consumerName: customerStepData.customerName,
              category: metadata.category,
              subCategory: metadata.subCategory,
              isVip: metadata.isVip || false,
              serviceAddress: metadata.serviceAddress,
            } as any);
          }
        }

        dataLoadedRef.current = true;
        setIsStepDataLoaded(true);
      } catch (error) {
        console.error("Failed to load request details step data:", error);
        dataLoadedRef.current = true;
        setIsStepDataLoaded(true);
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    requestForm,
    getStepData,
    getCustomerData,
    requestFields,
  ]);

  // Update form when configuration data is loaded
  useEffect(() => {
    if (utilityConfigData?.result && isStepDataLoaded) {
      const savedStepData = savedStepDataRef.current;

      const reasonField = requestFields.find(
        (field) => field.name === "reason",
      );
      if (reasonField) {
        const apiOptions = utilityConfigData.result.map((config) => ({
          label: config.name,
          value: config.id.toString(),
        }));

        if (savedStepData?.reasonLabel && savedStepData?.reason) {
          const savedOption = {
            label: savedStepData.reasonLabel,
            value: savedStepData.reason,
          };
          const optionExists = apiOptions.some(
            (option) => option.value === savedStepData.reason,
          );

          if (!optionExists) {
            reasonField.options = [...apiOptions, savedOption];
          } else {
            reasonField.options = apiOptions;
          }
        } else {
          reasonField.options = apiOptions;
        }
      }
    }
  }, [utilityConfigData, isStepDataLoaded, requestFields]);

  // Update form when time slot data is loaded
  useEffect(() => {
    if (timeSlotData?.result && isStepDataLoaded) {
      const savedStepData = savedStepDataRef.current;

      const timeSlotField = requestFields.find(
        (field) => field.name === "timeSlot",
      );
      if (timeSlotField) {
        const apiOptions = timeSlotData.result.map((slot) => ({
          label: slot.value,
          value: slot.key.toString(),
        }));

        if (savedStepData?.timeSlotLabel && savedStepData?.timeSlot) {
          const savedOption = {
            label: savedStepData.timeSlotLabel,
            value: savedStepData.timeSlot,
          };
          const optionExists = apiOptions.some(
            (option) => option.value === savedStepData.timeSlot,
          );

          if (!optionExists) {
            timeSlotField.options = [...apiOptions, savedOption];
          } else {
            timeSlotField.options = apiOptions;
          }
        } else {
          timeSlotField.options = apiOptions;
        }
      }
    }
  }, [timeSlotData, isStepDataLoaded, requestFields]);

  // Re-populate form with saved values after options are loaded
  useEffect(() => {
    if (isStepDataLoaded && !isConfigLoading && !isTimeSlotLoading) {
      const savedStepData = savedStepDataRef.current;
      if (savedStepData) {
        const formDataToSet = {
          reason: savedStepData.reason || "",
          timeSlot: savedStepData.timeSlot || "",
          scheduledDate: savedStepData.scheduledDate || null,
          consumerRemark: savedStepData.consumerRemark || "",
          accountStructurePreference:
            savedStepData.accountStructurePreference || "",
          serviceAreaPreference:
            savedStepData.serviceAreaPreference || "same_area",
          mailingAddressPreference:
            savedStepData.mailingAddressPreference || "same_as_service",
        };

        Object.entries(formDataToSet).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== "") {
            requestForm.setValue(key as any, value);
          }
        });
      }
    }
  }, [isStepDataLoaded, isConfigLoading, isTimeSlotLoading, requestForm]);

  // Set default accountStructurePreference when ownershipDisplay changes if not already set
  useEffect(() => {
    if (
      ownershipDisplay &&
      !requestForm.getValues("accountStructurePreference")
    ) {
      const defaultValue =
        ownershipDisplay === "Tenant"
          ? "same_owner"
          : ownershipDisplay === "Sub-Account"
            ? "same_master"
            : "individual_no_link";

      requestForm.setValue("accountStructurePreference", defaultValue);
    }
  }, [ownershipDisplay, requestForm]);

  // Reset form when consumer changes
  useEffect(() => {
    if (customerData?.selectedConsumerId && isStepDataLoaded) {
      const savedStepData = getStepData();
      if (
        savedStepData?.selectedConsumerId &&
        savedStepData.selectedConsumerId !== customerData.selectedConsumerId
      ) {
        console.log("Consumer changed, resetting request details form");
        requestForm.reset({
          requestType: "reconnect",
          utilitySupportRequest: 0,
          reason: "",
          priority: "medium",
          scheduledDate: null,
          timeSlot: "",
          consumerRemark: "",
          accountStructurePreference: "individual",
          serviceAreaPreference: "same_area",
          mailingAddressPreference: "same_as_service",
          newMasterAccountNumber: "",
          selectedOwnerId: "",
          selectedMaster: null,
          selectedOwner: null,
        });

        setSelectedOwner(null);
        setIsOwnerSelected(false);
        setSelectedMasterId(null);
        setMasterData(null); // ✅ Also clear masterData on consumer change

        saveStepData({
          selectedConsumerId: customerData.selectedConsumerId,
          requestType: "reconnect",
        });
      }
    }
  }, [
    customerData?.selectedConsumerId,
    isStepDataLoaded,
    requestForm,
    getStepData,
    saveStepData,
  ]);

  // Handle form submission
  const handleFormSubmit = useCallback(
    (data: CreateReconnectionRequestPayload) => {
      console.log("Submitting RequestDetailsStep data:", data);
      if (clearValidationError) {
        clearValidationError();
      }

      const reasonField = requestFields.find(
        (field) => field.name === "reason",
      );
      const timeSlotField = requestFields.find(
        (field) => field.name === "timeSlot",
      );

      const selectedReasonOption = reasonField?.options?.find(
        (option) => option.value === data.reason,
      );
      const selectedTimeSlotOption = timeSlotField?.options?.find(
        (option) => option.value === data.timeSlot,
      );

      const selectedReasonConfig = utilityConfigData?.result?.find(
        (config) => config.id.toString() === data.reason,
      );
      const extraData = selectedReasonConfig?.extraData || {};
      let reconnectionFee = 0;
      let feesBreakdown: any[] = [];

      if (extraData.fees && Array.isArray(extraData.fees)) {
        feesBreakdown = extraData.fees.map((f: any) => ({
          amount: Number(f.amount) || 0,
          feeType: f.fee_type || f.feeType || "Reconnection Fee",
        }));
        reconnectionFee = extraData.fees.reduce(
          (acc: number, f: any) => acc + (f.amount || 0),
          0,
        );
      } else if (extraData.amount) {
        feesBreakdown = [
          {
            amount: Number(extraData.amount) || 0,
            feeType: extraData.fee_type || extraData.feeType || "Standard",
          },
        ];
        reconnectionFee = Number(extraData.amount) || 0;
      } else {
        reconnectionFee = 0;
        feesBreakdown = [
          {
            amount: 0,
            feeType: "Standard Reconnection Fee",
          },
        ];
      }

      saveStepData({
        ...data,
        requestDate: new Date().toISOString(),
        consumerRemark: data.consumerRemark,
        requestType: "reconnect",
        scheduledDate: data.scheduledDate,
        timeSlot: data.timeSlot,
        reasonLabel: selectedReasonOption?.label || "",
        timeSlotLabel: selectedTimeSlotOption?.label || "",
        reconnectionFee,
        feesBreakdown,
        priority: extraData.priorityLevel || data.priority,
        priorityLevelDisplay: extraData.priorityLevelDisplay || "",
        ownershipDisplay,
        currentOwnerInfo: {
          ...customerData?.consumerMetadata,
          ownerName: customerData?.consumerMetadata?.ownerName || "N/A",
          ownerAccountNo:
            customerData?.consumerMetadata?.ownerAccountNo || "N/A",
          ownerAddress: customerData?.consumerMetadata?.ownerAddress || "N/A",
        },
        source: 1,
        remoteUtilityId: remoteUtilityId,
        utilitySupportRequest: Number(data.reason),
        additionalData: {
          preferredTimeSlot: Number(data.timeSlot),
          accountStructurePreference: data.accountStructurePreference,
          newMasterAccountNumber: data.newMasterAccountNumber,
          selectedOwnerId: data.selectedOwnerId,
          serviceAreaPreference: data.serviceAreaPreference,
          mailingAddressPreference: data.mailingAddressPreference,
        },
        serviceAddress: data.serviceAddress,
      });

      if (clearValidationError) {
        clearValidationError();
      }
      if (onNext) {
        onNext();
      }
    },
    [
      saveStepData,
      setValidationError,
      clearValidationError,
      onNext,
      requestForm,
      requestFields,
      remoteUtilityId,
    ],
  );

  // Handle field changes to save data automatically
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      if (isProcessingChangeRef.current) return;

      isProcessingChangeRef.current = true;

      try {
        const currentFormData = requestForm.getValues();

        let updatedData: any = {
          ...currentFormData,
          [fieldName]: value,
          ownershipDisplay,
          currentOwnerInfo: customerData?.consumerMetadata,
          lastUpdated: new Date().toISOString(),
        };

        if (fieldName === "reason") {
          const reasonField = requestFields.find(
            (field) => field.name === "reason",
          );
          const selectedOption = reasonField?.options?.find(
            (option) => option.value === value,
          );
          updatedData = {
            ...updatedData,
            reasonLabel: selectedOption?.label || "",
          };
        }

        if (fieldName === "timeSlot") {
          const timeSlotField = requestFields.find(
            (field) => field.name === "timeSlot",
          );
          const selectedOption = timeSlotField?.options?.find(
            (option) => option.value === value,
          );
          updatedData = {
            ...updatedData,
            timeSlotLabel: selectedOption?.label || "",
          };
        }

        if (fieldName === "accountStructurePreference") {
          requestForm.setValue("newMasterAccountNumber", "");
          requestForm.setValue("selectedOwnerId", "");
          setIsOwnerSelected(false);
          setSelectedOwner(null);
          setSelectedMasterId(null);
          setMasterData(null); // ✅ Clear masterData when preference changes
        }

        saveStepData(updatedData);
      } finally {
        setTimeout(() => {
          isProcessingChangeRef.current = false;
        }, 10);
      }
    },
    [
      requestForm,
      saveStepData,
      ownershipDisplay,
      customerData,
      setIsOwnerSelected,
      setSelectedOwner,
      requestFields,
    ],
  );

  const renderCustomFooter = useCallback(
    () => (
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <Button
          type="button"
          onClick={onPrevious}
          variant="outline"
          className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft size={16} />
          <span>Previous</span>
        </Button>

        <Button
          type="button"
          onClick={requestForm.handleSubmit(handleFormSubmit)}
          disabled={isValidating}
          className="flex items-center space-x-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 transition-colors"
        >
          <span>Next: Review</span>
          <ChevronRight size={16} />
        </Button>
      </div>
    ),
    [onPrevious, handleFormSubmit, requestForm, isValidating],
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-blue-600 p-1.5 rounded-lg">
          <FileText className="h-4 w-4 text-white" />
        </div>
        <h3 className="text-base font-black text-gray-800 uppercase tracking-wider">
          Request Details
        </h3>
      </div>

      {/* Customer Info Summary */}
      {customerData && (
        <div className="bg-[#F8FAFC] p-3.5 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
            <User className="h-3.5 w-3.5 text-blue-600" />
            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">
              Customer Information
            </h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ValueLabel
              label="Full Name"
              value={customerData.customerName ?? "NA"}
            />
            <ValueLabel
              label="Account Number"
              value={customerData.accountNumber ?? "NA"}
            />
            <ValueLabel
              label="Account Type"
              value={
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none font-black px-2"
                >
                  {ownershipDisplay}
                </Badge>
              }
            />
            <ValueLabel
              label="Primary Contact"
              value={customerData.contactPhone ?? "NA"}
            />
          </div>
        </div>
      )}

      {/* Dynamic Form */}
      <DynamicForm
        fields={requestFields}
        form={requestForm}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          showProgress: false,
          classes: FIELD_CLASSES,
        }}
        gridColumns={12}
        responsiveColumns={{ sm: 12, md: 12 }}
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
}
